import React, { useState, useRef } from "react";
import {
  Building2, Server, Users, Briefcase, Wallet, Palette, Rocket, Globe, CheckCircle2,
} from "lucide-react";
import { Button } from "./Button";
import { whitelabelContent } from "./i18n";

// >>> Optional: Endpoint deiner Software für White-Label-Anfragen.
// Leer lassen = Formular zeigt nur eine Erfolgsmeldung (ohne Versand).
const INQUIRY_ENDPOINT = "";

const AUDIENCE_ICONS = [Building2, Server, Users, Briefcase];
const BENEFIT_ICONS = [Wallet, Palette, Rocket, Globe];

const Label = ({ children }) => <label className="block text-sm font-medium text-[var(--brand-ink)] mb-1">{children}</label>;
const Input = (props) => <input {...props} className="w-full rounded-md border border-[var(--brand-border)] px-3 py-2 text-sm" />;
const Textarea = (props) => <textarea {...props} className="w-full rounded-md border border-[var(--brand-border)] px-3 py-2 text-sm" />;

const WhiteLabelPage = ({ onLogin, onBack, isLoggedIn, lang = "de" }) => {
  const t = whitelabelContent[lang];
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle");
  const formRef = useRef(null);
  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submitInquiry = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes("@")) { setStatus("error"); return; }
    setStatus("sending");
    try {
      if (INQUIRY_ENDPOINT) {
        await fetch(INQUIRY_ENDPOINT, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      }
      setStatus("sent");
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch {
      setStatus("error");
    }
  };
  return (
    <div className="min-h-screen bg-white overflow-x-hidden" data-testid="whitelabel-page">
      <nav className="flex items-center justify-between gap-2 px-4 sm:px-6 lg:px-12 py-5 max-w-6xl mx-auto">
        <div className="leading-tight min-w-0">
          <div className="font-[Playfair_Display] text-base sm:text-lg lg:text-xl font-bold text-[var(--brand-ink)]">{t.nav.brand}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--brand-muted)]">{t.nav.tagline}</div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button onClick={onBack} className="hidden sm:inline text-sm text-[var(--brand-ink)] hover:underline" data-testid="whitelabel-back-link">{t.nav.back}</button>
          <Button onClick={onLogin} size="sm" className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white rounded-full px-4 sm:px-5 text-sm whitespace-nowrap" data-testid="whitelabel-nav-app">{isLoggedIn ? t.nav.app : t.nav.login}</Button>
        </div>
      </nav>

      <header className="px-6 lg:px-12 py-16 lg:py-24" style={{ background: "linear-gradient(160deg, #2C3338 0%, #1B1F23 100%)" }}>
        <div className="max-w-4xl mx-auto text-center text-white">
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-white/60 mb-5">{t.hero.eyebrow}</span>
          <h1 lang={lang} className="font-[Playfair_Display] text-3xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 break-words">{t.hero.title[0]}<br />{t.hero.title[1]}</h1>
          <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-4">{t.hero.lead}</p>
          <p className="text-sm text-white/60 max-w-2xl mx-auto mb-9">{t.hero.sub}</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button onClick={scrollToForm} className="bg-white text-[var(--brand-ink)] hover:bg-white/90 rounded-full px-8 py-3 font-semibold" data-testid="whitelabel-hero-cta">{t.hero.cta}</Button>
            <Button onClick={onBack} variant="outline" className="border-white/40 text-white hover:bg-white/10 rounded-full px-8 py-3 font-semibold bg-transparent">{t.hero.ctaBack}</Button>
          </div>
        </div>
      </header>

      <section className="px-6 lg:px-12 py-16 max-w-6xl mx-auto">
        <h2 className="font-[Playfair_Display] text-3xl font-bold text-center text-[var(--brand-ink)] mb-3">{t.audience.heading}</h2>
        <p className="text-center text-[var(--brand-muted)] mb-12 max-w-xl mx-auto">{t.audience.sub}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.audience.items.map((a, i) => {
            const Icon = AUDIENCE_ICONS[i] || Building2;
            return (
              <div key={a.title} className="p-6 rounded-xl border border-[var(--brand-border)] hover:shadow-md transition-shadow">
                <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-3" style={{ background: "var(--brand-border-light)" }}><Icon className="w-5 h-5 text-[var(--brand-primary)]" /></div>
                <h3 className="font-semibold text-[var(--brand-ink)] mb-1">{a.title}</h3>
                <p className="text-sm text-[var(--brand-muted)]">{a.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="px-6 lg:px-12 py-16" style={{ background: "var(--brand-border-light)" }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="font-[Playfair_Display] text-3xl font-bold text-center text-[var(--brand-ink)] mb-12">{t.benefits.heading}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.benefits.items.map((b, i) => {
              const Icon = BENEFIT_ICONS[i] || Wallet;
              return (
                <div key={b.title} className="p-6 rounded-xl bg-white border border-[var(--brand-border)]">
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-3" style={{ background: "var(--brand-border-light)" }}><Icon className="w-5 h-5 text-[var(--brand-primary)]" /></div>
                  <h3 className="font-semibold text-[var(--brand-ink)] mb-1">{b.title}</h3>
                  <p className="text-sm text-[var(--brand-muted)]">{b.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-12 py-16 max-w-5xl mx-auto" data-testid="whitelabel-pricing">
        <div className="rounded-2xl overflow-hidden border border-[var(--brand-border)] grid grid-cols-1 md:grid-cols-5">
          <div className="md:col-span-3 p-8 lg:p-10">
            <h3 className="font-[Playfair_Display] text-2xl font-bold text-[var(--brand-ink)] mb-4">{t.pricing.heading}</h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {t.pricing.feats.map((ft) => (<li key={ft} className="flex items-start gap-2 text-sm text-[var(--brand-ink)]"><CheckCircle2 className="w-4 h-4 text-[var(--brand-primary)] mt-0.5 shrink-0" /> {ft}</li>))}
            </ul>
          </div>
          <div className="md:col-span-2 p-8 lg:p-10 flex flex-col justify-center text-white" style={{ background: "linear-gradient(160deg, #2C3338 0%, #1B1F23 100%)" }}>
            <p className="text-xs uppercase tracking-wider text-white/60 mb-3">{t.pricing.priceLabel}</p>
            <ul className="space-y-2 mb-4 text-sm">
              {t.pricing.rows.map((r) => (
                <li key={r.n} className="flex justify-between gap-3"><span className="text-white/75">{r.n}</span><span className="font-semibold">{r.p}</span></li>
              ))}
              <li className="flex justify-between gap-3 pt-2 border-t border-white/10"><span className="text-white/90 font-medium">{t.pricing.comboLabel}</span><span className="font-bold">{t.pricing.comboPrice} <span className="text-xs text-white/40 line-through font-normal">{t.pricing.comboOld}</span></span></li>
            </ul>
            <p className="text-xs text-white/60 mb-5">{t.pricing.priceNote}</p>
            <Button onClick={scrollToForm} className="w-full rounded-full bg-white text-[var(--brand-ink)] hover:bg-white/90 font-semibold" data-testid="whitelabel-cta">{t.pricing.cta}</Button>
          </div>
        </div>
        <p className="text-xs text-[var(--brand-muted)] mt-4 max-w-3xl" data-testid="whitelabel-license-note">
          {t.pricing.licenseNote.map((seg, i) => (seg.b ? <strong key={i}>{seg.t}</strong> : <React.Fragment key={i}>{seg.t}</React.Fragment>))}
        </p>
      </section>

      <section ref={formRef} className="px-6 lg:px-12 py-16" style={{ background: "var(--brand-border-light)" }} data-testid="whitelabel-form">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-[Playfair_Display] text-3xl font-bold text-center text-[var(--brand-ink)] mb-3">{t.form.heading}</h2>
          <p className="text-center text-[var(--brand-muted)] mb-8">{t.form.sub}</p>
          {status === "sent" ? (
            <div className="bg-white border border-[var(--brand-border)] rounded-2xl p-8 text-center" data-testid="whitelabel-form-success">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <h3 className="font-semibold text-lg text-[var(--brand-ink)] mb-1">{t.form.successTitle}</h3>
              <p className="text-sm text-[var(--brand-muted)]">{t.form.successText}</p>
            </div>
          ) : (
            <form onSubmit={submitInquiry} className="bg-white border border-[var(--brand-border)] rounded-2xl p-6 sm:p-8 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>{t.form.name}</Label><Input value={form.name} onChange={setField("name")} placeholder={t.form.namePh} required data-testid="wl-form-name" /></div>
                <div><Label>{t.form.email}</Label><Input type="email" value={form.email} onChange={setField("email")} placeholder={t.form.emailPh} required data-testid="wl-form-email" /></div>
                <div><Label>{t.form.company}</Label><Input value={form.company} onChange={setField("company")} placeholder={t.form.companyPh} data-testid="wl-form-company" /></div>
                <div><Label>{t.form.phone}</Label><Input value={form.phone} onChange={setField("phone")} placeholder={t.form.phonePh} data-testid="wl-form-phone" /></div>
              </div>
              <div><Label>{t.form.message}</Label><Textarea value={form.message} onChange={setField("message")} rows={4} placeholder={t.form.messagePh} data-testid="wl-form-message" /></div>
              {status === "error" && (<div className="text-sm rounded p-2" style={{ background: "#FEF2F2", color: "#991B1B" }} data-testid="wl-form-error">{t.form.error}</div>)}
              <Button type="submit" disabled={status === "sending"} className="w-full rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white font-semibold" data-testid="wl-form-submit">{status === "sending" ? t.form.sending : t.form.submit}</Button>
              <p className="text-[11px] text-center text-[var(--brand-muted)]">{t.form.privacy}</p>
            </form>
          )}
        </div>
      </section>

      <footer className="px-6 lg:px-12 py-8 text-center text-xs text-[var(--brand-muted)]">
        <button onClick={onBack} className="hover:text-[var(--brand-ink)] hover:underline">{t.footer.back}</button>
        <span className="mx-2">·</span>
        {t.footer.copyright(new Date().getFullYear())}
      </footer>
    </div>
  );
};

export default WhiteLabelPage;
